import { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  AlertCircle,
  Save,
  X,
  ArrowLeft,
  Upload,
  Undo,
  Redo,
  Download,
  FileEdit,
  FilePlus2,
  History,
  Info,
  MessageSquare,
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { EditSalesBudgetFormTM, flushPendingCcSalesBudgetSaves } from './edit-sales-budget-form-tm';
import { BudgetRevisionPanel } from './budget-revision-panel';
import type {
  BudgetData,
  SalesBudget,
  SalesCategory,
  BudgetRevision,
  RevisionReasonCategory,
  ResourceBudget,
} from '@/types/budget.types';
import { toast } from 'sonner';
import { fetchDefaultCategoriesAsSalesCategories } from '@/lib/data-service';
import type { CostCentre } from '@/components/blocks/cost-centres/cost-centres-table';
import type { HierarchicalCostCentre } from '@/components/blocks/cost-centres/cost-centres-hierarchical-table';

interface EditSalesBudgetPageProps {
  budgetData: BudgetData;
  projectStartDate?: string;
  projectEndDate?: string;
  /** When set, shows a contextual banner indicating the budget was loaded from this historical revision */
  loadedFromRevision?: BudgetRevision;
  /** When true, revision category is required before saving */
  requireRevisionNotes?: boolean;
  /** Used to derive hasResourceBudget for the labour lock */
  resourceBudget?: ResourceBudget;
  onSave: (updatedBudget: Partial<BudgetData>, revisionData?: Partial<BudgetRevision>) => Promise<void>;
  onCancel: () => void;
  /** When true, shows a distinct banner indicating the user is creating a new revision */
  isNewRevision?: boolean;
  /** Called when PM clicks "Make Active" — promotes a draft to the active revision */
  onMakeActive?: () => void;
  /** The designated cascade target category from Budget Settings */
  cascadeTargetCategoryId?: number | null;
  /** Cost Centre mode — when true, renders per-cost-centre budget rows instead of category rows */
  isCostCentreMode?: boolean;
  /** Flat list of cost centres for Cost Centre mode */
  costCentres?: CostCentre[];
  /** Hierarchical cost centres for Cost Centre mode structure view */
  hierarchicalCostCentres?: HierarchicalCostCentre[];
  /** When true, shows hierarchical/structure view in Cost Centre mode */
  isStructureView?: boolean;
}

export function EditSalesBudgetPage({
  budgetData,
  projectStartDate,
  projectEndDate,
  loadedFromRevision,
  isNewRevision = false,
  requireRevisionNotes = false,
  resourceBudget,
  onSave,
  onCancel,
  onMakeActive,
  cascadeTargetCategoryId,
  isCostCentreMode = false,
  costCentres = [],
  hierarchicalCostCentres = [],
  isStructureView = false,
}: EditSalesBudgetPageProps) {
  // Derive hasResourceBudget from the passed resourceBudget prop (or fall back to budgetData)
  const hasResourceBudget =
    (resourceBudget?.byRole?.length ?? budgetData.resourceBudget?.byRole?.length ?? 0) > 0;

  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showOptionalNotes, setShowOptionalNotes] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [salesBudgetChanges, setSalesBudgetChanges] = useState<SalesBudget | null>(null);
  const [currentBudgetData, setCurrentBudgetData] = useState<BudgetData>(budgetData);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] = useState(false);
  const [pendingSaveAsDraft, setPendingSaveAsDraft] = useState(false);

  // Fetch default GlobalCategories to auto-populate when no sales budget categories exist
  const [defaultSalesCategories, setDefaultSalesCategories] = useState<SalesCategory[]>([]);

  useEffect(() => {
    if (
      !currentBudgetData.salesBudget?.categories ||
      currentBudgetData.salesBudget.categories.length === 0
    ) {
      fetchDefaultCategoriesAsSalesCategories()
        .then(categories => {
          // Pre-populate Labour category budget from resource budget cascade values
          const roles = resourceBudget?.byRole ?? budgetData.resourceBudget?.byRole ?? [];
          if (roles.length > 0) {
            const labourTotal = roles.reduce(
              (sum, role) => sum + (role.budgetHours * role.billRate),
              0
            );
            if (labourTotal > 0) {
              categories = categories.map(cat =>
                cat.type === 'labour'
                  ? { ...cat, budget: labourTotal, remaining: labourTotal }
                  : cat
              );
            }
          }

          setDefaultSalesCategories(categories);
        })
        .catch(err => {
          console.warn('[EditSalesBudgetPage] Failed to load default GlobalCategories:', err);
        });
    }
  }, [currentBudgetData.salesBudget?.categories]);

  // Revision panel state
  const [reasonCategory, setReasonCategory] = useState<RevisionReasonCategory | ''>('');
  const [revisionReason, setRevisionReason] = useState('');

  // Undo/Redo state
  const MAX_HISTORY = 30;
  const [history, setHistory] = useState<SalesBudget[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [undoRedoVersion, setUndoRedoVersion] = useState(0);
  const isUndoRedoInProgress = useRef(false);

  // Clean up any stale Radix Dialog body styles on unmount.
  useEffect(() => {
    return () => {
      document.body.style.pointerEvents = '';
      document.body.removeAttribute('data-scroll-locked');
    };
  }, []);

  // Save handler — used for both normal Save and Save as Draft
  const handleSave = (asDraft = false) => {
    if (!isDirty) return;

    if (requireRevisionNotes && !reasonCategory) {
      setError('Please select a revision category before saving.');
      return;
    }

    setError(null);
    setPendingSaveAsDraft(asDraft);
    performSave(asDraft);
  };

  const performSave = async (asDraft: boolean) => {
    try {
      setIsSaving(true);
      setError(null);

      // Flush any pending debounced CC budget API writes so the data is
      // persisted before the view re-fetches after save.
      if (isCostCentreMode) {
        await flushPendingCcSalesBudgetSaves();
      }

      const updatedBudget: Partial<BudgetData> = {};
      if (salesBudgetChanges) {
        updatedBudget.salesBudget = salesBudgetChanges;
      }

      const revisionData: Partial<BudgetRevision> = {
        reasonCategory: reasonCategory as RevisionReasonCategory,
        reason: revisionReason,
        status: asDraft ? 'draft' : 'active',
        draftType: null,
      };

      await onSave(updatedBudget, revisionData);

      toast.success(asDraft ? 'Draft saved' : 'Sales budget revision saved', {
        description: asDraft
          ? 'This revision is saved as a draft and is not yet active.'
          : reasonCategory
          ? `Saved with category: "${reasonCategory}"`
          : undefined,
      });

      setIsDirty(false);
      setSalesBudgetChanges(null);
      setReasonCategory('');
      setRevisionReason('');
      setPendingSaveAsDraft(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save budget');
    } finally {
      setIsSaving(false);
    }
  };

  const handleBack = () => {
    if (isDirty) {
      setShowUnsavedChangesDialog(true);
    } else {
      onCancel();
    }
  };

  const handleCancel = () => {
    if (isDirty) {
      setShowUnsavedChangesDialog(true);
    } else {
      onCancel();
    }
  };

  const handleConfirmLeave = () => {
    setShowUnsavedChangesDialog(false);
    onCancel();
  };

  // Undo handler
  const handleUndo = useCallback(() => {
    if (historyIndex > 0) {
      isUndoRedoInProgress.current = true;
      const newIndex = historyIndex - 1;
      const previousState = history[newIndex];
      setHistoryIndex(newIndex);
      setSalesBudgetChanges(previousState);
      setCurrentBudgetData(prev => ({ ...prev, salesBudget: previousState }));
      setIsDirty(newIndex > 0);
      setUndoRedoVersion(v => v + 1);
      setTimeout(() => { isUndoRedoInProgress.current = false; }, 100);
    }
  }, [historyIndex, history]);

  // Redo handler
  const handleRedo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      isUndoRedoInProgress.current = true;
      const newIndex = historyIndex + 1;
      const nextState = history[newIndex];
      setHistoryIndex(newIndex);
      setSalesBudgetChanges(nextState);
      setCurrentBudgetData(prev => ({ ...prev, salesBudget: nextState }));
      setIsDirty(true);
      setUndoRedoVersion(v => v + 1);
      setTimeout(() => { isUndoRedoInProgress.current = false; }, 100);
    }
  }, [historyIndex, history]);

  // Keyboard shortcuts for undo/redo
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        handleUndo();
      } else if (
        (e.ctrlKey || e.metaKey) &&
        ((e.shiftKey && e.key === 'z') || e.key === 'y')
      ) {
        e.preventDefault();
        handleRedo();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleUndo, handleRedo]);

  // Get available categories from the actual budget data (with monthlyBudgets from API)
  // Fall back to default GlobalCategories if no categories exist in the budget data
  // Deduplicate by name to prevent duplicate rows from appearing in the edit form
  const availableCategories = (() => {
    const cats =
      currentBudgetData.salesBudget?.categories &&
      currentBudgetData.salesBudget.categories.length > 0
        ? currentBudgetData.salesBudget.categories
        : defaultSalesCategories;
    const seen = new Set<string>();
    return cats.filter(cat => {
      if (seen.has(cat.name)) return false;
      seen.add(cat.name);
      return true;
    });
  })();

  return (
    <div className="min-h-screen bg-background flex flex-col rounded-lg">
      {/* Header */}
      <div className="bg-background border-b">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={handleBack}
                disabled={isSaving}
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold flex items-center gap-2">
                  {loadedFromRevision ? (
                    <><History className="h-5 w-5 text-blue-500" /> Edit Sales Budget</>
                  ) : isNewRevision ? (
                    <><FilePlus2 className="h-5 w-5 text-emerald-600" /> New Sales Budget Revision</>
                  ) : (
                    <><FileEdit className="h-5 w-5" /> Edit Sales Budget</>
                  )}
                </h1>
                <p className="text-sm text-muted-foreground mt-1">
                  Update sales budget values for {currentBudgetData.projectName}
                </p>
              </div>

              {/* Compact Unsaved Changes Indicator */}
              {isDirty && (
                <div className="flex items-center gap-1.5 px-2.5 py-1 bg-warning-50 border border-warning-200 rounded-md text-warning-700 text-sm">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  <span className="whitespace-nowrap">Unsaved changes</span>
                </div>
              )}
            </div>

            {/* Import and Export Buttons */}
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => toast.info('Export feature coming soon')}
                disabled={isSaving}
                title="Export budget"
              >
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={() => toast.info('Import feature coming soon')}
                disabled={isSaving}
              >
                <Upload className="h-4 w-4 mr-2" />
                Import
              </Button>
            </div>
          </div>

          {/* Error Alert */}
          {error && (
            <div className="mt-3">
              <Alert variant="destructive" showIcon={false}>
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            </div>
          )}
        </div>
      </div>

      {/* Loaded from historical revision banner */}
      {loadedFromRevision && (
        <div className="bg-blue-50 border-b border-blue-200 px-4 py-2.5 flex items-center gap-2 text-blue-800 text-sm">
          <Info className="h-4 w-4 flex-shrink-0 text-blue-500" />
          <span>
            Loaded from{' '}
            <strong>SB-v{loadedFromRevision.revisionNumber}</strong>
            {loadedFromRevision.date
              ? ` — ${new Date(loadedFromRevision.date).toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric',
                })}`
              : ''}
            . You are editing a copy of this historical revision. Saving will create a new draft.
          </span>
        </div>
      )}

      {/* New Revision banner */}
      {isNewRevision && !loadedFromRevision && (
        <div className="bg-emerald-50 border-b border-emerald-200 px-4 py-2.5 flex items-center gap-2 text-emerald-800 text-sm">
          <FilePlus2 className="h-4 w-4 flex-shrink-0 text-emerald-600" />
          <span>
            <strong>Creating New Revision</strong> — You are working on a new sales budget revision. Your changes will be saved as a new draft revision.
          </span>
        </div>
      )}

      {/* Main Content - Scrollable */}
      <div className="flex-1 overflow-y-auto">
        <div className="px-4 py-4 space-y-6">
          <EditSalesBudgetFormTM
            key={`sales-${undoRedoVersion}`}
            projectId={budgetData.projectId}
            salesBudget={currentBudgetData.salesBudget}
            projectStartDate={projectStartDate}
            projectEndDate={projectEndDate}
            availableCategories={availableCategories}
            resourceBudget={currentBudgetData.resourceBudget}
            hasResourceBudget={hasResourceBudget}
            cascadeTargetCategoryId={cascadeTargetCategoryId}
            isCostCentreMode={isCostCentreMode}
            costCentres={costCentres}
            hierarchicalCostCentres={hierarchicalCostCentres}
            isStructureView={isStructureView}
            onChange={(changes) => {
              setSalesBudgetChanges(changes);

              if (isUndoRedoInProgress.current) return;

              if (!hasUserInteracted) {
                setHasUserInteracted(true);
                setHistory([currentBudgetData.salesBudget, changes]);
                setHistoryIndex(1);
                setIsDirty(true);
                return;
              }

              setIsDirty(true);

              const newHistory = history.slice(0, historyIndex + 1);
              newHistory.push(changes);

              if (newHistory.length > MAX_HISTORY) {
                newHistory.shift();
                setHistory(newHistory);
                setHistoryIndex(newHistory.length - 1);
              } else {
                setHistory(newHistory);
                setHistoryIndex(newHistory.length - 1);
              }
            }}
          />

          {/* Revision panel — required when requireRevisionNotes is on, optional otherwise */}
          {isDirty && requireRevisionNotes && (
            <BudgetRevisionPanel
              changePercentage={0}
              requiresApproval={false}
              reasonCategory={reasonCategory}
              onReasonCategoryChange={setReasonCategory}
              revisionReason={revisionReason}
              onRevisionReasonChange={setRevisionReason}
              costBudgetChanges={null}
              salesBudgetChanges={salesBudgetChanges}
              resourceBudgetChanges={null}
              originalBudget={budgetData}
            />
          )}

          {/* Optional notes when requireRevisionNotes is off */}
          {isDirty && !requireRevisionNotes && (
            <div className="border rounded-lg p-4 bg-muted/30">
              <button
                type="button"
                className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-2 w-full text-left"
                onClick={() => setShowOptionalNotes(prev => !prev)}
              >
                <MessageSquare className="h-4 w-4" />
                {showOptionalNotes ? 'Hide notes' : 'Add notes (optional)'}
              </button>
              {showOptionalNotes && (
                <div className="mt-3 space-y-3">
                  <BudgetRevisionPanel
                    changePercentage={0}
                    requiresApproval={false}
                    reasonCategory={reasonCategory}
                    onReasonCategoryChange={setReasonCategory}
                    revisionReason={revisionReason}
                    onRevisionReasonChange={setRevisionReason}
                    costBudgetChanges={null}
                    salesBudgetChanges={salesBudgetChanges}
                    resourceBudgetChanges={null}
                    originalBudget={budgetData}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Fixed Footer */}
      <div className="sticky bottom-0 z-10 bg-background border-t shadow-lg">
        <div className="px-4 py-3">
          <div className="flex justify-end items-center gap-3">
            <Button
              variant="outline"
              onClick={handleCancel}
              disabled={isSaving}
              size="lg"
            >
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>

            <Button
              variant="outline"
              onClick={handleUndo}
              disabled={historyIndex <= 0 || isSaving}
              size="lg"
              title="Undo (Ctrl+Z)"
            >
              <Undo className="h-4 w-4 mr-2" />
              Undo {historyIndex > 0 && `(${historyIndex})`}
            </Button>

            <Button
              variant="outline"
              onClick={handleRedo}
              disabled={historyIndex >= history.length - 1 || isSaving}
              size="lg"
              title="Redo (Ctrl+Shift+Z)"
            >
              <Redo className="h-4 w-4 mr-2" />
              Redo {historyIndex < history.length - 1 && `(${history.length - 1 - historyIndex})`}
            </Button>

            {loadedFromRevision || onMakeActive ? (
              <>
                {/* Draft / historical revision mode — Save as Draft + Make Active */}
                <Button
                  variant="outline"
                  onClick={() => handleSave(true)}
                  disabled={!isDirty || isSaving}
                  size="lg"
                >
                  <FileEdit className="h-4 w-4 mr-2" />
                  {isSaving && pendingSaveAsDraft ? 'Saving…' : 'Save as Draft'}
                </Button>

                <Button
                  onClick={onMakeActive}
                  disabled={!isDirty || isSaving}
                  size="lg"
                  className="bg-success-600 hover:bg-success-700 text-white"
                >
                  Make Active
                </Button>
              </>
            ) : (
              /* Normal edit of active revision — single Save button */
              <Button
                onClick={() => handleSave(false)}
                disabled={!isDirty || isSaving}
                size="lg"
              >
                <Save className="h-4 w-4 mr-2" />
                {isSaving && !pendingSaveAsDraft ? 'Saving…' : 'Save Budget'}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Unsaved Changes Dialog */}
      <AlertDialog open={showUnsavedChangesDialog} onOpenChange={setShowUnsavedChangesDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure that you want to leave the current page? The changes that you made won't be saved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmLeave}>Leave</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
